import { authConfig } from "../auth/authConfig";
import { getSsoBootstrapToken } from "../auth/ssoAuth";
import { loadSettings } from "./settingsStorage";
import { loadTemplates } from "../templates/templatesStorage";
// Thrown from plain functions, not components — hence the i18next singleton
// rather than the useTranslation hook.
import { i18n } from "../../i18n";
import { FALLBACK } from "../../i18n/locales";

// Uploads the user's LOX24 settings + templates so the backend can send SMS for
// calendar events while they're offline (REQ-CAL-008).
//
// Only ever called while calendar automation is enabled — a user who doesn't use
// automation has no API key stored server-side at all, and disabling it purges
// what was stored.

export class SettingsSyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SettingsSyncError";
  }
}

const SYNC_STAMP_KEY = "lox24.settingsSyncedAt";

/** When settings last reached the backend, for the status line in Settings. */
export function getLastSyncedAt(): string | undefined {
  try {
    return localStorage.getItem(SYNC_STAMP_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function setLastSyncedAt(iso: string): void {
  try {
    localStorage.setItem(SYNC_STAMP_KEY, iso);
  } catch {
    // A browser blocking add-in storage only costs us the status line.
  }
}

export function clearLastSyncedAt(): void {
  try {
    localStorage.removeItem(SYNC_STAMP_KEY);
  } catch {
    // nothing to clear
  }
}

/**
 * Push the current settings and templates to the backend. Throws
 * SettingsSyncError with a message suitable for display.
 */
export async function syncSettingsToBackend(): Promise<void> {
  if (!authConfig.backendUrl) {
    throw new SettingsSyncError(i18n.t("errors.noBackend"));
  }

  let ssoToken: string;
  try {
    ssoToken = await getSsoBootstrapToken();
  } catch {
    throw new SettingsSyncError(i18n.t("errors.workAccountRequired"));
  }

  const settings = loadSettings();
  const body = {
    apiKey: settings.apiKey,
    senderId: settings.senderId,
    smsType: settings.smsType,
    encoding: settings.encoding,
    defaultCountry: settings.defaultCountry,
    signature: settings.signature,
    // The user's IANA zone (e.g. "Asia/Colombo"). Graph returns event times in
    // UTC, so without this the server would render dates in UTC rather than the
    // time the user actually sees in Outlook.
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // So the server formats {Date} and {Time} in this user's language.
    language: i18n.language || FALLBACK,
    templates: loadTemplates(),
  };

  let res: Response;
  try {
    res = await fetch(`${authConfig.backendUrl}/api/settings/sync`, {
      method: "POST",
      headers: { Authorization: `Bearer ${ssoToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new SettingsSyncError(
      i18n.t("errors.serverUnreachable", { detail: (err as Error).message })
    );
  }

  if (!res.ok) {
    const detail = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new SettingsSyncError(
      detail.message ?? detail.error ?? i18n.t("errors.syncFailed", { status: res.status })
    );
  }

  setLastSyncedAt(new Date().toISOString());
}

/**
 * Sync only when automation is on, and never let a failure block the local save
 * — returns the error message instead of throwing, so callers can warn.
 */
export async function syncIfAutomationEnabled(): Promise<string | undefined> {
  if (!loadSettings().calendarAutomation) {
    return undefined;
  }
  try {
    await syncSettingsToBackend();
    return undefined;
  } catch (err) {
    return (err as Error).message;
  }
}
